import { Injectable, inject, isDevMode } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs';
import { namespacedStorageKey } from '../auth/storage-namespace';

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private static readonly SESSION_RELOADED_VERSION_KEY =
    'nutritionapp.pwa.reloadedVersionHash';

  private readonly swUpdate = inject(SwUpdate);
  private started = false;
  private checking = false;

  start(): void {
    if (this.started) return;
    this.started = true;

    if (isDevMode() || !this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe((event) => void this.applyUpdateAndReload(event));

    void this.checkForUpdates();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void this.checkForUpdates();
      }
    });
  }

  private async applyUpdateAndReload(event: VersionReadyEvent): Promise<void> {
    const latestHash = event.latestVersion.hash;
    const storageKey = namespacedStorageKey(AppUpdateService.SESSION_RELOADED_VERSION_KEY);
    const lastReloadedHash = sessionStorage.getItem(
      storageKey,
    );

    // Evita ciclos de recarga si el navegador reemite VERSION_READY para el mismo hash.
    if (lastReloadedHash === latestHash) return;

    sessionStorage.setItem(storageKey, latestHash);

    try {
      await this.swUpdate.activateUpdate();
      location.reload();
    } catch {
      // Si falla activateUpdate, recargar igual suele tomar la nueva versión disponible.
      location.reload();
    }
  }

  private async checkForUpdates(): Promise<void> {
    if (this.checking) return;
    this.checking = true;
    try {
      await this.swUpdate.checkForUpdate();
    } catch {
      // Errores de red o del SW no deben romper la app.
    } finally {
      this.checking = false;
    }
  }
}
